import {
  type ElementType,
  type MouseEvent,
  type ReactNode,
  useState,
} from "react";
import { useSelector } from "react-redux";
import {
  Link as RouterLink,
  type NavigateFunction,
  useNavigate,
} from "react-router-dom";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import DataUsageIcon from "@mui/icons-material/DataUsage";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import LockPersonIcon from "@mui/icons-material/LockPerson";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import {
  AppBar,
  Avatar,
  Box,
  Breadcrumbs,
  IconButton,
  Link,
  SvgIcon,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import queryString from "query-string";

import {
  type RouteCrumb,
  useRouteTrail,
} from "@/apps/admin/components/PageHeader/useRouteTrail";
import ProfileMenu from "@/apps/admin/components/ProfileMenu/ProfileMenu";
import { paths } from "@/apps/admin/configurations/paths";
import { OBJECT_NAME } from "@/apps/admin/enums/objectName";
import { OBJECT_TYPE } from "@/apps/admin/enums/objectType";
import { canUseGenieKnowledge } from "@/apps/admin/features/authentication/GenieKnowledgeGate/GenieKnowledgeGate";
import { checkGlobalManagementRolePermission } from "@/apps/admin/utilities/data";
import { ReactComponent as TSCLogoText } from "@/common/assets/vectors/tsc_logo_text.svg";
import { ASK_GENIE_RESIZING_ATTRIBUTE } from "@/common/genie/dock/genieDockLayout";
import { default as QuickAccessMenu } from "@/ui/components/QuickAccessMenu/QuickAccessMenu";
import { VerticalDivider } from "@/ui/components/VerticalDivider/VerticalDivider";

import {
  selectPermissions,
  selectUser,
} from "../../features/authentication/authenticationSlice";

export const topMenuHeight = "64px";

/**
 * `genieLauncher`: the Genie button, owned by GlobalLayout with the dialog it
 * opens. `rightInset`: room left on the right for a docked Genie, so the bar
 * and its buttons stay beside it.
 */
type QuickAccessOption = {
  title: string;
  icon: ElementType;
  onClick: () => void;
};

type TopNavProps = {
  genieLauncher?: ReactNode;
  rightInset?: string;
};

const TopNav = ({ genieLauncher = null, rightInset = "0px" }: TopNavProps) => {
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const [profileMenuAnchor, setProfileMenuAnchor] =
    useState<HTMLElement | null>(null);
  const profileMenuOpen = Boolean(profileMenuAnchor);
  const handleOpenProfileMenu = (event: MouseEvent<HTMLElement>) =>
    setProfileMenuAnchor(event.currentTarget);
  const handleCloseProfileMenu = () => setProfileMenuAnchor(null);
  const userPermissions = useSelector(selectPermissions);

  const quickaccessOptions = buildQuickAccessOptions(userPermissions, navigate);

  if (!user) return null;
  return (
    <AppBar
      position="fixed"
      sx={{
        backgroundColor: "white",
        zIndex: (theme) => theme.zIndex.drawer + 1,
        width: "auto",
        left: 0,
        right: rightInset,
        transition: (theme) =>
          theme.transitions.create("right", {
            easing: theme.transitions.easing.easeIn,
            duration: theme.transitions.duration.leavingScreen,
          }),
        [`html[${ASK_GENIE_RESIZING_ATTRIBUTE}] &`]: { transition: "none" },
      }}
    >
      <Toolbar disableGutters sx={{ px: "8px", alignItems: "center", gap: 1 }}>
        <Box p={1}>
          <RouterLink to="/">
            <SvgIcon
              component={TSCLogoText}
              viewBox="0 0 67 39"
              sx={{ width: "67px", height: "39px", ml: "6px", mt: "1px" }}
            />
          </RouterLink>
        </Box>
        <RouteTrail />
        <Box minWidth={{ xs: "8px", sm: "56px" }} flexGrow={1} />
        {genieLauncher}
        {quickaccessOptions.length > 0 && (
          <QuickAccessMenu options={quickaccessOptions} />
        )}
        <VerticalDivider />
        <Tooltip title="Profile & Account Settings">
          <IconButton onClick={handleOpenProfileMenu}>
            <Avatar
              src={user.photoURL ?? undefined}
              imgProps={{ referrerPolicy: "no-referrer" }}
              sx={{ width: 32, height: 32 }}
            />
          </IconButton>
        </Tooltip>
      </Toolbar>
      <ProfileMenu
        anchor={profileMenuAnchor}
        open={profileMenuOpen}
        onClose={handleCloseProfileMenu}
      />
    </AppBar>
  );
};

export default TopNav;

/** A crumb's text: its entity's name when its route names one, else the route's title. */
const CrumbText = ({ crumb }: { crumb: RouteCrumb }) =>
  crumb.Label === null ? (
    crumb.label
  ) : (
    <crumb.Label params={crumb.params} title={crumb.label} />
  );

/** Where the page sits: each parent links back to its page, the current one is plain text. */
export const RouteTrail = () => {
  const trail = useRouteTrail();
  const current = trail.at(-1);
  if (!current) {
    return null;
  }
  return (
    <Breadcrumbs
      aria-label="breadcrumb"
      separator="›"
      data-testid="adminRouteTrail"
      sx={{ minWidth: 0, "& .MuiBreadcrumbs-ol": { flexWrap: "nowrap" } }}
    >
      {trail.slice(0, -1).map((crumb) =>
        crumb.href === null ? (
          <Typography key={crumb.label} color="text.secondary" noWrap>
            <CrumbText crumb={crumb} />
          </Typography>
        ) : (
          <Link
            key={crumb.href}
            component={RouterLink}
            to={crumb.href}
            color="text.secondary"
            underline="hover"
            noWrap
          >
            <CrumbText crumb={crumb} />
          </Link>
        )
      )}
      <Typography
        component="div"
        color="text.primary"
        fontWeight={500}
        noWrap
        aria-current="page"
        data-testid="adminRouteTrailCurrent"
      >
        <CrumbText crumb={current} />
      </Typography>
    </Breadcrumbs>
  );
};

export const buildQuickAccessOptions = (
  userPermissions: unknown,
  onClick: NavigateFunction
): QuickAccessOption[] => {
  const options: QuickAccessOption[] = [
    {
      title: "RBAC",
      icon: LockPersonIcon,
      onClick: () => onClick(`/rbac`),
    },
    {
      title: "Stakeholders",
      icon: PersonOutlineIcon,
      onClick: () =>
        onClick(
          `/stakeholders?${queryString.stringify(
            paths.STAKEHOLDERS.defaultQueryParams
          )}`
        ),
    },
    {
      title: "Scrapers",
      icon: HubOutlinedIcon,
      onClick: () => onClick(`/scrapers`),
    },
  ];

  // TSC admin management only (global management.manage), as chat-service's
  // ManagementManageGuard checks on every route the page reads.
  if (canUseGenieKnowledge(userPermissions)) {
    options.push({
      title: "Genie behaviour & knowledge",
      icon: AutoAwesomeOutlinedIcon,
      onClick: () => onClick(`/${paths.GENIE_KNOWLEDGE.path}`),
    });
  }

  // Platform admins (chat-service's AdminGuard); changes need management.manage.
  if (
    checkGlobalManagementRolePermission(
      userPermissions,
      OBJECT_TYPE.MANAGEMENT,
      OBJECT_NAME.READ
    )
  ) {
    options.push({
      title: "GeniePlus Usage",
      icon: DataUsageIcon,
      onClick: () => onClick(`/${paths.GENIE_PLUS_USAGE.path}`),
    });
  }

  if (
    checkGlobalManagementRolePermission(
      userPermissions,
      OBJECT_TYPE.ORGANIZATION,
      OBJECT_NAME.CREATE
    )
  ) {
    options.push({
      title: "Organizations",
      icon: HomeOutlinedIcon,
      onClick: () => onClick("/organizations"),
    });
  }
  return options;
};
